import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, AlertCircle, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import axios from 'axios';
import { format, addDays, differenceInDays, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryCodes = [
  { value: '+1', label: '+1 (Canada/US)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+61', label: '+61 (Australia)' },
  { value: '+49', label: '+49 (Germany)' },
  { value: '+33', label: '+33 (France)' },
];

const defaultFormData = {
  // Contact Info
  first_name: '',
  last_name: '',
  address: '',
  unit: '',
  city: '',
  province_state: '',
  country: 'Canada',
  postal_code: '',
  country_code: '+1',
  home_phone: '',
  cell_phone: '',
  business_phone: '',
  business_ext: '',
  ext_contact_name: '',
  fax: '',
  email: '',
  referred_by: '',
  comments: '',
  landlord_comments: '',
  
  // Cottage Info
  cottage: 'Big Bear',
  reason_for_renting: '',
  tenant_name: '',
  tenant_start_date: '',
  num_guests: '',
  age_range: '',
  check_in_date: '',
  check_in_time: '4:30PM',
  check_out_date: '',
  check_out_time: '11:00AM',
  rental_amount: '',
  hst_percent: 13,
  hst_type: 'percent',
  
  // Payments
  payment_1_amount: '',
  payment_1_date: '',
  payment_2_amount: '',
  payment_2_date: '',
  payment_3_amount: '',
  payment_3_date: '',
  security_deposit: '',
  
  // Credit Info
  credit_card_type: '',
  credit_card_number: '',
  credit_card_expiry: '',
  credit_card_cvv: '',
  cardholder_name: '',
  cardholder_address: '',
  cardholder_city: '',
  cardholder_postal: '',
};

export default function RenterForm({ renter, onSave, onCancel }) {
  const [formData, setFormData] = useState(renter || defaultFormData);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [dateConflict, setDateConflict] = useState(null);
  const [checkingDates, setCheckingDates] = useState(false);
  const [pricingData, setPricingData] = useState([]);
  const [calculatedPricing, setCalculatedPricing] = useState(null);

  // Fetch pricing data on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${API}/pricing`);
        setPricingData(res.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  // Get weekly rate based on check-in date using stored date ranges
  const getWeeklyRate = (checkInDate) => {
    if (!checkInDate || pricingData.length === 0) return null;
    
    const date = parseISO(checkInDate);
    const checkInMonth = date.getMonth() + 1; // 1-12
    const checkInDay = date.getDate();
    
    let rate = 8000; // Default fallback
    let matchedSeason = null;
    
    // Find matching season based on stored date ranges
    for (const season of pricingData) {
      const startDateStr = season.start_date;
      const endDateStr = season.end_date;
      
      if (!startDateStr || !endDateStr) continue;
      
      try {
        const startParts = startDateStr.split('-');
        const endParts = endDateStr.split('-');
        
        const startMonth = parseInt(startParts[0]);
        const startDay = parseInt(startParts[1]);
        const endMonth = parseInt(endParts[0]);
        const endDay = parseInt(endParts[1]);
        
        let inRange = false;
        
        // Check if check-in date falls within this season's range
        if (startMonth <= endMonth) {
          // Normal range within same year (e.g., May 18 - June 14)
          inRange = (
            (checkInMonth > startMonth || (checkInMonth === startMonth && checkInDay >= startDay)) &&
            (checkInMonth < endMonth || (checkInMonth === endMonth && checkInDay <= endDay))
          );
        } else {
          // Year wrap-around range (e.g., Oct 13 - May 17)
          inRange = (
            (checkInMonth > startMonth || (checkInMonth === startMonth && checkInDay >= startDay)) ||
            (checkInMonth < endMonth || (checkInMonth === endMonth && checkInDay <= endDay))
          );
        }
        
        if (inRange) {
          rate = season.price;
          matchedSeason = season.season_name;
          break;
        }
      } catch (e) {
        console.error('Error parsing season dates:', e);
      }
    }
    
    return rate;
  };

  // Check for date conflicts when dates change
  useEffect(() => {
    const checkDates = async () => {
      if (!formData.check_in_date || !formData.check_out_date) {
        setDateConflict(null);
        return;
      }

      setCheckingDates(true);
      try {
        const params = {
          check_in: formData.check_in_date,
          check_out: formData.check_out_date,
        };
        
        // If editing, exclude this renter's own dates
        if (renter?.id) {
          params.exclude_renter_id = renter.id;
        }

        const response = await axios.get(`${API}/availability/check-dates`, { params });
        
        if (response.data.status !== 'available') {
          setDateConflict(response.data);
        } else {
          setDateConflict(null);
        }
      } catch (error) {
        console.error('Error checking dates:', error);
      } finally {
        setCheckingDates(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkDates, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.check_in_date, formData.check_out_date, renter?.id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Manual calculate prices button handler
  const handleCalculatePrices = () => {
    if (!formData.check_in_date || !formData.check_out_date) {
      alert('Please enter both Check-in and Check-out dates first');
      return;
    }

    const checkIn = parseISO(formData.check_in_date);
    const checkOut = parseISO(formData.check_out_date);
    const days = differenceInDays(checkOut, checkIn);
    const weeks = Math.ceil(days / 7);
    
    if (weeks <= 0) {
      alert('Check-out date must be after Check-in date');
      return;
    }

    const weeklyRate = getWeeklyRate(formData.check_in_date);
    const rentalAmount = weeklyRate * weeks;
    const hstAmount = rentalAmount * 0.13; // 13% HST
    const totalWithHst = rentalAmount + hstAmount;
    
    // Payment schedule: 25%, 25%, 50%
    const payment1 = Math.round(totalWithHst * 0.25 * 100) / 100;
    const payment2 = Math.round(totalWithHst * 0.25 * 100) / 100;
    const payment3 = Math.round(totalWithHst * 0.50 * 100) / 100;
    
    // Payment dates
    const today = format(new Date(), 'yyyy-MM-dd');
    const payment2Date = format(addDays(checkIn, -90), 'yyyy-MM-dd'); // 90 days before check-in
    const payment3Date = format(addDays(checkIn, -60), 'yyyy-MM-dd'); // 60 days before check-in

    // Update form with calculated values
    setFormData(prev => ({
      ...prev,
      rental_amount: rentalAmount,
      hst_percent: 13,
      hst_type: 'percent',
      payment_1_amount: payment1,
      payment_1_date: today,
      payment_2_amount: payment2,
      payment_2_date: payment2Date,
      payment_3_amount: payment3,
      payment_3_date: payment3Date,
    }));

    // Show calculation summary
    setCalculatedPricing({
      weeks,
      weeklyRate,
      rentalAmount,
      hstAmount,
      totalWithHst,
      payment1,
      payment2,
      payment3,
      payment1Date: today,
      payment2Date,
      payment3Date,
    });
  };

  const calculateTotal = () => {
    const rental = parseFloat(formData.rental_amount) || 0;
    const hstPercent = parseFloat(formData.hst_percent) || 0;
    
    if (formData.hst_type === 'percent') {
      return rental + (rental * hstPercent / 100);
    }
    return rental + hstPercent;
  };

  const calculateRemaining = () => {
    const total = calculateTotal();
    const p1 = parseFloat(formData.payment_1_amount) || 0;
    const p2 = parseFloat(formData.payment_2_amount) || 0;
    const p3 = parseFloat(formData.payment_3_amount) || 0;
    return total - p1 - p2 - p3;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      alert('Please enter first and last name');
      return;
    }

    // Warn about date conflicts (booked dates block submission, pending just warn)
    if (dateConflict?.status === 'booked') {
      if (!window.confirm('WARNING: Some of these dates are already BOOKED by another renter. Creating this renter will cause a scheduling conflict.\n\nAre you sure you want to proceed?')) {
        return;
      }
    } else if (dateConflict?.status === 'pending') {
      if (!window.confirm(`Note: There ${dateConflict.pending_inquiry_count === 1 ? 'is' : 'are'} ${dateConflict.pending_inquiry_count} pending inquir${dateConflict.pending_inquiry_count === 1 ? 'y' : 'ies'} for some of these dates.\n\nProceed with creating this renter?`)) {
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        rental_amount: parseFloat(formData.rental_amount) || 0,
        num_guests: parseInt(formData.num_guests) || 0,
        hst_percent: parseFloat(formData.hst_percent) || 0,
        total_with_hst: calculateTotal(),
        payment_1_amount: parseFloat(formData.payment_1_amount) || 0,
        payment_2_amount: parseFloat(formData.payment_2_amount) || 0,
        payment_3_amount: parseFloat(formData.payment_3_amount) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
      };
      await onSave(dataToSave);
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount || 0);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="contact">Contact Info</TabsTrigger>
          <TabsTrigger value="cottage">Cottage Info</TabsTrigger>
          <TabsTrigger value="credit">Renter Credit Info</TabsTrigger>
          <TabsTrigger value="cardholder">Credit Cardholder Info</TabsTrigger>
        </TabsList>

        {/* CONTACT INFO TAB */}
        <TabsContent value="contact" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Renter&apos;s First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2 md:col-span-3">
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Input
                value={formData.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Prov/State *</Label>
              <Input
                value={formData.province_state}
                onChange={(e) => handleChange('province_state', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Country *</Label>
              <Input
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Postal/Zip *</Label>
              <Input
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Country Code</Label>
              <Select
                value={formData.country_code}
                onValueChange={(v) => handleChange('country_code', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countryCodes.map(code => (
                    <SelectItem key={code.value} value={code.value}>
                      {code.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Home Phone *</Label>
              <Input
                value={formData.home_phone}
                onChange={(e) => handleChange('home_phone', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Cell Phone</Label>
              <Input
                value={formData.cell_phone}
                onChange={(e) => handleChange('cell_phone', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Business Phone</Label>
              <Input
                value={formData.business_phone}
                onChange={(e) => handleChange('business_phone', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Ext.</Label>
              <Input
                value={formData.business_ext}
                onChange={(e) => handleChange('business_ext', e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Name of person at ext.</Label>
              <Input
                value={formData.ext_contact_name}
                onChange={(e) => handleChange('ext_contact_name', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Fax</Label>
              <Input
                value={formData.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Referred By</Label>
            <Input
              value={formData.referred_by}
              onChange={(e) => handleChange('referred_by', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Comments</Label>
            <Textarea
              value={formData.comments}
              onChange={(e) => handleChange('comments', e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Landlord Comments</Label>
            <Textarea
              value={formData.landlord_comments}
              onChange={(e) => handleChange('landlord_comments', e.target.value)}
              rows={3}
            />
          </div>
        </TabsContent>

        {/* COTTAGE INFO TAB */}
        <TabsContent value="cottage" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cottage</Label>
              <Select
                value={formData.cottage}
                onValueChange={(v) => handleChange('cottage', v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Big Bear">Big Bear</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason for renting</Label>
              <Input
                value={formData.reason_for_renting}
                onChange={(e) => handleChange('reason_for_renting', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tenant</Label>
              <Input
                value={formData.tenant_name}
                onChange={(e) => handleChange('tenant_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Tenant Start Date</Label>
              <Input
                type="date"
                value={formData.tenant_start_date}
                onChange={(e) => handleChange('tenant_start_date', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>No. of Guests</Label>
              <Input
                type="number"
                min="1"
                max="14"
                value={formData.num_guests}
                onChange={(e) => handleChange('num_guests', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Age Range</Label>
              <Input
                value={formData.age_range}
                onChange={(e) => handleChange('age_range', e.target.value)}
                placeholder="e.g., 12-80"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Check-in Date *</Label>
              <Input
                type="date"
                value={formData.check_in_date}
                onChange={(e) => handleChange('check_in_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Check-in Time *</Label>
              <Input
                value={formData.check_in_time}
                onChange={(e) => handleChange('check_in_time', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Check-out Date *</Label>
              <Input
                type="date"
                value={formData.check_out_date}
                onChange={(e) => handleChange('check_out_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Check-out Time *</Label>
              <Input
                value={formData.check_out_time}
                onChange={(e) => handleChange('check_out_time', e.target.value)}
              />
            </div>
          </div>

          {/* Date Conflict Warning */}
          {checkingDates && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking date availability...
            </div>
          )}
          
          {dateConflict && !checkingDates && (
            <Alert variant={dateConflict.status === 'booked' ? 'destructive' : 'default'} className={dateConflict.status === 'pending' ? 'border-amber-500 bg-amber-50' : ''}>
              {dateConflict.status === 'booked' ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              )}
              <AlertTitle className={dateConflict.status === 'pending' ? 'text-amber-800' : ''}>
                {dateConflict.status === 'booked' ? 'Date Conflict!' : 'Pending Inquiries'}
              </AlertTitle>
              <AlertDescription className={dateConflict.status === 'pending' ? 'text-amber-700' : ''}>
                {dateConflict.status === 'booked' ? (
                  <>
                    These dates are already booked: {dateConflict.booked_dates?.slice(0, 3).join(', ')}
                    {dateConflict.booked_dates?.length > 3 && ` and ${dateConflict.booked_dates.length - 3} more`}
                  </>
                ) : (
                  <>
                    There {dateConflict.pending_inquiry_count === 1 ? 'is' : 'are'} {dateConflict.pending_inquiry_count} pending inquir{dateConflict.pending_inquiry_count === 1 ? 'y' : 'ies'} for some of these dates.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Calculate Prices Button */}
          <div className="flex items-center gap-4">
            <Button
              type="button"
              onClick={handleCalculatePrices}
              className="bg-[#1B4D3E] hover:bg-[#153E32]"
            >
              <Calculator className="w-4 h-4 mr-2" />
              Calculate Prices
            </Button>
            <span className="text-sm text-gray-500">
              Click to auto-populate rental amount, HST, and payment schedule based on dates
            </span>
          </div>

          {/* Calculated Pricing Display */}
          {calculatedPricing && (
            <Alert className="border-green-500 bg-green-50">
              <Calculator className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">Calculated Pricing</AlertTitle>
              <AlertDescription className="text-green-700">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                  <div><strong>{calculatedPricing.weeks} week(s)</strong> @ ${calculatedPricing.weeklyRate?.toLocaleString()}/week</div>
                  <div>Rental: <strong>${calculatedPricing.rentalAmount?.toLocaleString()}</strong></div>
                  <div>HST (13%): <strong>${calculatedPricing.hstAmount?.toLocaleString()}</strong></div>
                  <div>Total: <strong>${calculatedPricing.totalWithHst?.toLocaleString()}</strong></div>
                </div>
                <div className="mt-2 text-xs text-green-600">
                  Payments set: 25% today, 25% at 90 days before, 50% at 60 days before check-in
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Rental Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.rental_amount}
                onChange={(e) => handleChange('rental_amount', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>HST</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.hst_percent}
                onChange={(e) => handleChange('hst_percent', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>HST Type</Label>
              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.hst_type === 'percent'}
                    onChange={() => handleChange('hst_type', 'percent')}
                  />
                  <span>%</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.hst_type === 'flat'}
                    onChange={() => handleChange('hst_type', 'flat')}
                  />
                  <span>Flat</span>
                </label>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="bg-[#F0EBE5] p-4 rounded-sm space-y-2">
            <p className="text-sm">
              <strong>Total owing with HST:</strong> {formatCurrency(calculateTotal())}
              {' '}
              <strong>Remaining after deposits below:</strong> {formatCurrency(calculateRemaining())}
            </p>
          </div>

          {/* Payment Schedule */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>1st payment amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.payment_1_amount}
                onChange={(e) => handleChange('payment_1_amount', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>1st payment date</Label>
              <Input
                type="date"
                value={formData.payment_1_date}
                onChange={(e) => handleChange('payment_1_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>2nd payment amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.payment_2_amount}
                onChange={(e) => handleChange('payment_2_amount', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>2nd payment date</Label>
              <Input
                type="date"
                value={formData.payment_2_date}
                onChange={(e) => handleChange('payment_2_date', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>3rd payment amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.payment_3_amount}
                onChange={(e) => handleChange('payment_3_amount', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>3rd payment date</Label>
              <Input
                type="date"
                value={formData.payment_3_date}
                onChange={(e) => handleChange('payment_3_date', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Security Deposit ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.security_deposit}
              onChange={(e) => handleChange('security_deposit', e.target.value)}
            />
          </div>
        </TabsContent>

        {/* RENTER CREDIT INFO TAB */}
        <TabsContent value="credit" className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-sm mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> Credit card information is sensitive. 
              Ensure this is only accessible to authorized personnel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Credit Card Type</Label>
              <Select
                value={formData.credit_card_type}
                onValueChange={(v) => handleChange('credit_card_type', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select card type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Visa">Visa</SelectItem>
                  <SelectItem value="Mastercard">Mastercard</SelectItem>
                  <SelectItem value="Amex">American Express</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Credit Card Number</Label>
              <Input
                value={formData.credit_card_number}
                onChange={(e) => handleChange('credit_card_number', e.target.value)}
                placeholder="**** **** **** ****"
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                value={formData.credit_card_expiry}
                onChange={(e) => handleChange('credit_card_expiry', e.target.value)}
                placeholder="MM/YY"
              />
            </div>
            <div className="space-y-2">
              <Label>CVV</Label>
              <Input
                value={formData.credit_card_cvv}
                onChange={(e) => handleChange('credit_card_cvv', e.target.value)}
                placeholder="***"
                maxLength={4}
              />
            </div>
          </div>
        </TabsContent>

        {/* CREDIT CARDHOLDER INFO TAB */}
        <TabsContent value="cardholder" className="space-y-6">
          <div className="space-y-2">
            <Label>Cardholder Name</Label>
            <Input
              value={formData.cardholder_name}
              onChange={(e) => handleChange('cardholder_name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Cardholder Address</Label>
            <Input
              value={formData.cardholder_address}
              onChange={(e) => handleChange('cardholder_address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={formData.cardholder_city}
                onChange={(e) => handleChange('cardholder_city', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Postal Code</Label>
              <Input
                value={formData.cardholder_postal}
                onChange={(e) => handleChange('cardholder_postal', e.target.value)}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Submit Buttons */}
      <div className="flex gap-3 justify-end mt-8 pt-6 border-t border-[#E2E0DC]">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#1B4D3E] hover:bg-[#153E32]"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </form>
  );
}
